import React, { useState, useEffect, useCallback } from 'react';
import '../../../scss/_mobiledraw.scss';
import { MobileDrawHeader } from './components/MobileDrawHeader';
import { applicationInsights } from '../../../../ApplicationInsights';
import { DrawApi } from '@apply/api';
import { useDeriveDrawItemsFromDrawBudgetId } from '../../../../home_v2/hooks/useDeriveDrawItemsFromDrawBudgetId';
import { IDrawBudget, IPostSaveDrawItem } from '@apply/api/src/api/DrawApi';
import { MobileDrawDetails } from './components/MobileDrawDetails/MobileDrawDetails';
import { Button, ButtonTheme, useWaitOverlayStore } from '@apply/components';
import { useNavigate } from 'react-router-dom';
import { ConfirmV2 } from '@apply/ui';
import { MobileDrawItem } from './types/MobileDrawItem';
import { useDenyGCAccessIfUnauthorized } from '../../../functions/gcDrawAuthorization';

export const MobileDrawControl: React.FC = () => {
    const { setWaiting } = useWaitOverlayStore.getState();

    const navigate = useNavigate();
    const drawBudgetId = parseInt(location.pathname.split('/')[2]);
    const drawApi = new DrawApi.DrawApi(applicationInsights);

    const [drawBudget, set_drawBudget] = useState<IDrawBudget>();
    const { items: derivedItems, isLoading } = useDeriveDrawItemsFromDrawBudgetId(drawBudgetId);

    const [mobileDrawItems, set_mobileDrawItems] = useState<MobileDrawItem[]>([]);
    const [hideFullyFunded, set_hideFullyFunded] = useState(true);
    const [totalDrawAmount, set_totalDrawAmount] = useState<number>(0);
    const [openConfirmDelete, set_openConfirmDelete] = useState(false);
    const [isLoadingDelayed, set_isLoadingDelayed] = useState(true);

    const denyGCAccess = useDenyGCAccessIfUnauthorized(drawBudget?.canGCAddDraw);

    setWaiting(isLoading, 'Loading...');
    useEffect(() => {
        if (!drawBudget) return;

        if (denyGCAccess) {
            window.location.href = '/home';
            return;
        } else {
            //Locate current draw and detect whether or not we should redirect to signing
            const latestDraw = drawBudget.draws[drawBudget.draws?.length - 1];

            if (latestDraw && (latestDraw.borrowerSubmitted || latestDraw.needsBorrowerSignature)) {
                navigate(`/mobilereviewandsubmit/${drawBudget.id}`);
                return;
            }
        }

        set_isLoadingDelayed(false);
    }, [isLoading, drawBudget, denyGCAccess]);

    useEffect(() => {
        set_mobileDrawItems(derivedItems);
    }, [derivedItems]);

    useEffect(() => {
        const total = mobileDrawItems?.reduce((sum, value) => (sum += value?.borrowerRequest ?? 0), 0);
        set_totalDrawAmount(total);
    }, [mobileDrawItems]);

    const loadBudget = async () => {
        const response = await drawApi.getDrawBudget(drawBudgetId);
        if (response.statusCode !== 200) return;

        const budgetResult = response.result;
        set_drawBudget(budgetResult);
    };

    useEffect(() => {
        loadBudget();
    }, [drawBudgetId]);

    const set_mobileBorrowerRequest = useCallback((drawItemId: number, borrowerRequest: number) => {
        set_mobileDrawItems((items) =>
            items.map((item) => (item.id === drawItemId ? { ...item, borrowerRequest, dirty: true } : item))
        );
    }, []);

    const saveMobileDraw = async () => {
        const itemsToSave: IPostSaveDrawItem[] = mobileDrawItems
            .filter((item) => item.dirty)
            .map((item) => {
                return {
                    drawBudgetItemId: item.drawBudgetItemId,
                    drawId: item.drawId,
                    drawNumber: item.drawNumber,
                    value: item.borrowerRequest,
                };
            });

        if (itemsToSave.length === 0) return;

        await drawApi.drawItemBorrowerRequest_v2_mobile(itemsToSave);
    };

    const onReviewAndSubmitClick = async () => {
        await saveMobileDraw();
        navigate(`/mobilereviewandsubmit/${drawBudgetId}`);
    };

    const handleConfirmDelete = async () => {
        if (!drawBudget) return;
        const latestDraw = drawBudget.draws[drawBudget.draws?.length - 1];
        if (!latestDraw) throw new Error('latestDraw required');

        const response = await drawApi.deleteDraw(latestDraw.id);

        if (response.statusCode !== 200) {
            alert(response.errors?.[0] || response.statusCode);
            return;
        }

        if (!response.result) return;

        navigate('/home');
    };

    const onCancelDrawClick = async () => {
        set_openConfirmDelete(true);
    };

    const handleCancelDelete = async () => {
        set_openConfirmDelete(false);
    };

    const cancelDrawDisabled = () => {
        if (!drawBudget) return;
        const latestDraw = drawBudget.draws[drawBudget.draws?.length - 1];
        if (!latestDraw) throw new Error('latestDraw required');

        if (latestDraw.isFinalized || latestDraw.inspectionOrdered) {
            return true;
        } else {
            return false;
        }
    };

    if (isLoadingDelayed) return;

    return (
        <main style={{ padding: 0 }}>
            <MobileDrawHeader
                drawBudget={drawBudget}
                mobileDrawItems={mobileDrawItems}
                hideFullyFunded={hideFullyFunded}
                onToggleFullyFunded={set_hideFullyFunded}
                saveMobileDraw={saveMobileDraw}
            />
            <MobileDrawDetails
                drawBudget={drawBudget}
                mobileDrawItems={mobileDrawItems}
                hideFullyFunded={hideFullyFunded}
                set_mobileBorrowerRequest={set_mobileBorrowerRequest}
            />
            <Button
                id='saveFinishButton'
                theme={ButtonTheme.Primary}
                width={'80%'}
                handleClick={onReviewAndSubmitClick}
                disabled={totalDrawAmount <= 0}
            >
                Review and Submit
            </Button>
            <Button
                id='cancelDrawButton'
                theme={ButtonTheme.LinkRed}
                width={'80%'}
                handleClick={onCancelDrawClick}
                disabled={cancelDrawDisabled()}
            >
                Cancel Draw
            </Button>
            <ConfirmV2
                id='confirmCancelDraw'
                open={openConfirmDelete}
                title='Cancel Draw'
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                okLabel='Yes'
                cancelLabel='No'
                width='340px'
                height='200px'
            >
                Are you sure you wish to delete this draw? (Draw #{drawBudget?.drawCount})
            </ConfirmV2>
        </main>
    );
};
